import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { lt } from '@mews/framework';
import { Stack, Table, TableActiveColumnFilters, TableProvider, Typography, useTableState } from '@optimus-web/core';
import { useEnterprisesProductContext } from '../../../../context/EnterprisesProductContext';
import { TaxConfiguration, useConfigureProductTax } from '../../../../hooks/useConfigureProductTax';
import { useColumns } from './useColumns';

export const TaxationMultiplePropertiesForm = () => {
    const {
        state: { enterprises },
    } = useEnterprisesProductContext();
    const {
        formState: { isSubmitting, isValid },
    } = useFormContext();

    const taxEnvironmentCodes = enterprises.map(({ taxEnvironmentCode }) => taxEnvironmentCode);

    const { searchTaxRates, taxConfigurationList } = useConfigureProductTax(taxEnvironmentCodes);

    const [tableState, setTableState] = useTableState<TaxConfiguration>({
        pagination: {
            show: false,
        },
    });

    const selectedTaxEnvironments = useMemo(() => {
        const taxEnvironments = new Set<string>();

        taxConfigurationList.forEach((tax) => {
            taxEnvironments.add(tax.legalEnvironmentName || '');
        });

        return Array.from(taxEnvironments);
    }, [taxConfigurationList]);

    const columns = useColumns({ searchTaxRates, selectedTaxEnvironments });

    const filteredData = useMemo(() => {
        const legalEnvironmentFilter = tableState.filters?.legalEnvironmentName;

        if (!legalEnvironmentFilter?.length) {
            return taxConfigurationList;
        }

        return taxConfigurationList.filter(
            (row) =>
                !legalEnvironmentFilter?.length ||
                (legalEnvironmentFilter[0].value as string[]).includes(row.legalEnvironmentName || '')
        );
    }, [tableState.filters?.legalEnvironmentName, taxConfigurationList]);

    useEffect(() => {
        if (!isSubmitting) return;

        if (isValid) return;

        // Form submission failed due to validation errors, reset filters to show all inputs in the tables.
        setTableState((state) => ({
            ...state,
            filters: {
                legalEnvironmentName: [],
            },
        }));
    }, [isSubmitting, isValid, setTableState]);

    return (
        <Stack vertical spacing="300">
            <Stack vertical spacing="200">
                <Typography textStyle="titleSmallStrong" as="div">
                    {lt('ConfigureProductSubHeadingTax')}
                </Typography>
                <Typography textStyle="bodyMedium" variant="secondary" as="div">
                    {lt('ConfigureProductTaxBody')}
                </Typography>
            </Stack>
            <TableProvider columns={columns} onStateChange={setTableState} state={tableState} as="div">
                <TableActiveColumnFilters />
                <Table
                    amountOfRows={filteredData.length || 0}
                    data={filteredData}
                    fixedFirstColumn
                    uniqueRowId="code"
                />
            </TableProvider>
        </Stack>
    );
};
